import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import IconPlayButtonTriangle from '@/svgs/play-button-triangle.svg'
import Video from '@/components/Video'

import classes from './ProductFullBleedHero.module.scss'
import ResponsiveComponent from '@/components/ResponsiveComponent'
import imageUrlBuilder from '@sanity/image-url'
import sanityClient from '@/services/sanityClient'

const ProductFullBleedHero = ({ fields, imagePriority }) => {
  const [startVideo, setStartVideo] = useState(false)
  const builder = imageUrlBuilder(sanityClient)

  const { heroType, fullBleedHero, splitHero, questionnairetHero } = fields

  let {
    title,
    heroStyle,
    smallerText,
    textColor,
    desktopBackgroundImage,
    mobileBackgroundImage,
    imageBrightness,
    alt,
    youtubeVideoId,
    topMargin,
    bottomMargin,
    fixedImage,
  } = fullBleedHero

  function urlFor(source) {
    return builder.image(source)
  }

  /**
   * Generates a cropped image URL based on hotspot and crop data.
   * @param {object} image - The image object with asset, hotspot, and crop info.
   * @returns {string|undefined} The cropped image URL or undefined if image data is invalid.
   */
  function cropImageUrl(image) {
    const { asset, hotspot, crop } = image || {};

    if (!asset?.metadata?.dimensions || !hotspot) {
      return asset?.url;
    }

    const { height: h, width: w } = asset.metadata.dimensions;
    const cropLeft = parseInt(crop.left * w);
    const cropTop = parseInt(crop.top * h);
    const hotspotWidth = parseInt(hotspot.width * w);
    const hotspotHeight = parseInt(hotspot.height * h);

    return urlFor(image)
        .rect(cropLeft, cropTop, hotspotWidth, hotspotHeight)
        .fit('max')
        .auto('format')
        .url();
  }

  const showVideo = () => {
    setStartVideo(true)
  }

  let btnColor
  // Conditionally change the color of the button
  if (textColor === 'text--light') {
    btnColor = 'salmon'
  } else {
    btnColor = 'salmon'
  }

  const imageInlineStyles = {
    filter: `brightness(${imageBrightness ? imageBrightness : 80}%)`,
  }

  const mobileImageInlineStyles = {
    filter: `brightness(${imageBrightness ? imageBrightness : 80}%)`,
  }

  return (
    <div
      className={`${classes['hero']} ${smallerText ? classes['hero--smaller-text'] : ''} ${classes[heroStyle]} ${heroStyle === 'hero--with-container' ? 'container' : ''} ${classes[textColor]} ${topMargin ? classes['top-margin'] : ''} ${bottomMargin ? classes['bottom-margin'] : ''} ${startVideo ? classes['hero--video-enabled'] : ''}`}
    >
      <div className={`${classes['hero__text']}`}>
        <div className={classes['hero__text-inner']}>
          {fields.fullBleedHero.header && (
            <h1
              className={`${classes['hero__header']}_${classes[textColor]} ${classes[`${heroStyle === 'hero--bottom' ? 'heading--catch' : ''}`]}`}
            >
              {fields.fullBleedHero.header}
            </h1>
          )}
          {fields.subheader && (
            <h2 className={classes['hero__subheader']}>{fields.subheader}</h2>
          )}

        </div>
      </div>
      <div className={classes['hero__main-wrap']}>
        {youtubeVideoId && heroStyle === 'hero--with-container' && (
          <button
            onClick={() => showVideo()}
            className={`${classes['hero-video__action-btn']} btn ${btnColor}`}
          >
            <IconPlayButtonTriangle />
            <span>Play Video</span>
          </button>
        )}

        <ResponsiveComponent
          desktopBreakpoint={'sm'}
          mobile={
            mobileBackgroundImage && (
              <div
                className={`${classes['hero__wrap']} ${
                  fixedImage ? classes['hero__wrap--mbl'] : ''
                }`}
              >
                <Image
                  className={classes.mbl__img}
                  src={cropImageUrl(mobileBackgroundImage)}
                  fill
                  objectFit="cover"
                  priority={imagePriority}
                  alt={alt || title}
                  style={mobileImageInlineStyles}
                />
              </div>
            )
          }
          desktop={
            desktopBackgroundImage && (
              <div
                className={`${classes['hero__wrap']} ${classes['hero__wrap--dsktp']}`}
              >
                <Image
                  className={classes.mbl__img}
                  src={cropImageUrl(desktopBackgroundImage)}
                  fill
                  objectFit="cover"
                  priority={imagePriority}
                  alt={alt || title}
                  style={imageInlineStyles}
                />
              </div>
            )
          }
        />
      </div>

      {youtubeVideoId && (
        <Video
          youtubeVideoId={youtubeVideoId}
          startVideo={startVideo}
          className={classes['hero-video__wrap']}
        />
      )}
    </div>
  )
}

export default ProductFullBleedHero
